import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Stars from "@/components/Stars";
import { MessageSquareQuote, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });

function ReviewForm({ sellerId, productId, mine, onSaved }) {
  const [rating, setRating] = useState(mine?.rating || 0);
  const [text, setText] = useState(mine?.text || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!rating) { toast.error("Kies een aantal sterren"); return; }
    setSaving(true);
    try {
      await api.post("/reviews", { seller_id: sellerId, rating, text, product_id: productId });
      toast.success(mine ? "Beoordeling bijgewerkt" : "Bedankt voor je beoordeling!");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon beoordeling niet opslaan");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="review-form" className="rounded-xl border border-amber-800/40 bg-amber-950/10 p-4 space-y-3">
      <div className="text-sm font-medium text-zinc-200">{mine ? "Jouw beoordeling aanpassen" : "Laat een beoordeling achter"}</div>
      <Stars value={rating} onChange={setRating} size="h-6 w-6" testid="review-star" />
      <textarea data-testid="review-text-input" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} placeholder="Hoe was je ervaring? (optioneel)"
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-amber-700 focus:outline-none" />
      <button data-testid="review-submit-button" onClick={save} disabled={saving} className="rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 text-sm font-medium px-4 py-2 disabled:opacity-60">
        {saving ? "Opslaan..." : mine ? "Bijwerken" : "Plaats beoordeling"}
      </button>
    </div>
  );
}

function ReplyBox({ review, onSaved }) {
  const [text, setText] = useState("");
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/reviews/${review.id}/reply`, { text });
      toast.success("Reactie geplaatst");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon reactie niet plaatsen");
    }
  };
  return (
    <div className="mt-3 flex gap-2">
      <input data-testid={`review-reply-input-${review.id}`} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Reageer openbaar (eenmalig)..."
        className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none" />
      <button data-testid={`review-reply-submit-${review.id}`} onClick={send} className="rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm px-3 py-2">Reageer</button>
    </div>
  );
}

function ReviewItem({ r, isSeller, isMine, onChange }) {
  const remove = async () => {
    if (!window.confirm("Beoordeling verwijderen?")) return;
    try {
      await api.delete(`/reviews/${r.id}`);
      toast.success("Beoordeling verwijderd");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon beoordeling niet verwijderen");
    }
  };
  return (
    <div data-testid={`review-item-${r.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Stars value={r.rating} size="h-3.5 w-3.5" />
          <span className="text-sm text-zinc-200">{r.reviewer_name || "Gebruiker"}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          {fmtDate(r.created_at)}
          {isMine && <button data-testid={`review-delete-${r.id}`} onClick={remove} className="text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
      {r.text && <p className="mt-2 text-sm text-zinc-400 whitespace-pre-line">{r.text}</p>}
      {r.product_title && <p className="mt-1 text-[11px] text-zinc-600">over: {r.product_title}</p>}
      {r.reply ? (
        <div data-testid={`review-reply-${r.id}`} className="mt-3 rounded-lg border-l-2 border-rose-600 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-300">
          <span className="text-xs font-medium text-rose-300">Reactie van de aanbieder</span>
          <p className="mt-0.5 whitespace-pre-line">{r.reply}</p>
        </div>
      ) : isSeller && <ReplyBox review={r} onSaved={onChange} />}
    </div>
  );
}

export default function SellerReviews({ sellerId, sellerName, productId, onStats }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [elig, setElig] = useState(null);
  const isSeller = user && user.id === sellerId;

  const load = useCallback(() => {
    api.get(`/users/${sellerId}/reviews`).then(({ data }) => {
      setData(data);
      if (onStats) onStats({ avg: data.avg, count: data.count });
    });
    if (user && user.id !== sellerId) api.get(`/reviews/eligibility/${sellerId}`).then(({ data }) => setElig(data)).catch(() => {});
  }, [sellerId, user, onStats]);

  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  return (
    <section data-testid="seller-reviews" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-zinc-100">Beoordelingen van {sellerName || "de aanbieder"}</h2>
          <div className="mt-2 flex items-center gap-3">
            <Stars value={data.avg} size="h-5 w-5" testid="seller-rating-stars" />
            <span data-testid="seller-rating-summary" className="text-sm text-zinc-400">
              {data.count ? <><span className="font-mono text-amber-300">{data.avg.toFixed(1)}</span> · {data.count} {data.count === 1 ? "beoordeling" : "beoordelingen"}</> : "Nog geen beoordelingen"}
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
        <div>
          {elig?.can_review ? (
            <ReviewForm key={elig.my_review?.id || "new"} sellerId={sellerId} productId={productId} mine={elig.my_review} onSaved={load} />
          ) : isSeller ? (
            <div data-testid="review-seller-note" className="flex items-start gap-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-500">
              <MessageSquareQuote className="h-4 w-4 mt-0.5 text-rose-400 shrink-0" />
              {data.count ? "Je kunt op elke beoordeling één keer openbaar reageren." : "Je hebt nog geen beoordelingen. Na een chatgesprek kunnen klanten je beoordelen."}
            </div>
          ) : (
            <div data-testid="review-locked-note" className="flex items-start gap-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-500">
              <MessageSquareQuote className="h-4 w-4 mt-0.5 text-zinc-600 shrink-0" />
              {user ? "Je kunt een beoordeling achterlaten nadat je via de chat contact hebt gehad met deze aanbieder." : "Log in en chat met deze aanbieder om een beoordeling achter te laten."}
            </div>
          )}
        </div>
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <ReviewItem key={r.id} r={r} isSeller={isSeller} isMine={elig?.my_review?.id === r.id} onChange={load} />
          ))}
        </div>
      </div>
    </section>
  );
}
